/**
 * @file text-on-fill.ts
 * @description Контраст цвета текста («Авто», роль или свой) на заливке
 * поверхности — общая функция для вида корзины (`theme.cart`, SB120 этап 4)
 * и баннера согласия на cookie (`theme.consentBanner`, phase19.16 Р3г, SB134;
 * db-design-r3g §2.3 «контраст как у корзины»).
 *
 * Зачем отдельный модуль: до Р3г функция жила внутри cart.ts. Баннер должен
 * предупреждать о контрасте тем же правилом, что корзина (одни пороги, одно
 * «не повторять предупреждение темы»), поэтому правило вынесено, а cart.ts
 * пользуется им без изменения поведения.
 */

import { compositeColors, contrastRatio } from "../cabinet-theme/contrast.js";
import { requireHexColor } from "./color.js";
import { SITE_CONTRAST_RULES } from "./contrast.js";
import type { SiteColors } from "./keys.js";
import { fillContrastIssue, resolveSurfaceFill, type SiteSurfaceFill } from "./surface-fill.js";
import type { SiteAutoTextColor } from "./text-color.js";

/**
 * Пара «роль текста на роли-заливке» уже проверяется правилами контраста темы
 * (`SITE_CONTRAST_RULES`, kind `text`): иначе владелец увидел бы два
 * предупреждения — темы и поверхности — и «!» на умолчании (ревью
 * 2026-10-19). Пары вне правил поверхность проверяет сама.
 */
function coveredByThemeRules(color: SiteAutoTextColor, fill: SiteSurfaceFill): boolean {
  if (color.kind !== "role" || fill.kind !== "role") return false;
  return SITE_CONTRAST_RULES.some(
    (rule) => rule.kind === "text" && rule.role === color.role && rule.against.includes(fill.role),
  );
}

/**
 * Контраст цвета текста на заливке; null — проверять нечего сверх правил
 * темы или цвет битый. «Авто» — тот же ответ, что у шапки и подвала
 * (`fillContrastIssue`): у ролей-поверхностей пару уже проверяют правила
 * контраста темы; явная роль на роли — так же, если пара есть в правилах темы.
 */
export function autoTextOnFillRatio(
  color: SiteAutoTextColor,
  fill: SiteSurfaceFill,
  theme: { readonly colors: SiteColors },
): number | null {
  if (color.kind === "auto") return fillContrastIssue(fill, theme)?.ratio ?? null;
  if (coveredByThemeRules(color, fill)) return null;
  try {
    const bg = requireHexColor(resolveSurfaceFill(fill, theme).background);
    const raw = color.kind === "role" ? theme.colors[color.role] : color.color;
    return contrastRatio(compositeColors(requireHexColor(raw), bg), bg);
  } catch {
    return null;
  }
}

/** Контраст до десятых с отбрасыванием (как `formatContrast`: 4,49 → 4,4). */
export function floorContrastTenth(ratio: number): number {
  return Math.floor(ratio * 10) / 10;
}
